import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';

const SEAT_STATUS = {
    AVAILABLE: 'AVAILABLE',
    OCCUPIED: 'OCCUPIED',
    RESERVED: 'RESERVED',
    SELECTED: 'SELECTED',
    DISABLED: 'DISABLED',
};

const seatVariants = {
    hidden: { scale: 0.5, opacity: 0 },
    visible: { scale: 1, opacity: 1 },
};

// maksimalno trajanje rezervacije – pun krug
const MAX_RESERVATION_DURATION = 180;

export const Seat = ({ seat, onSelect, status, onTimerEnd }) => {
    const isSelectable =
        status === SEAT_STATUS.AVAILABLE || status === SEAT_STATUS.SELECTED;
    const isTaken =
        status === SEAT_STATUS.OCCUPIED || status === SEAT_STATUS.RESERVED;
    const isDisabled = status === SEAT_STATUS.DISABLED;

    const [remainingTime, setRemainingTime] = useState(
        seat.reservationExpireSec ?? MAX_RESERVATION_DURATION
    );

    // Ako se promeni seat ili novo preostalo vreme stigne sa backenda
    useEffect(() => {
        if (seat.reservationExpireSec != null) {
            setRemainingTime(seat.reservationExpireSec);
        } else {
            setRemainingTime(MAX_RESERVATION_DURATION);
        }
    }, [seat.id, seat.reservationExpireSec]);

    // Timer koji koristi apsolutno vreme isteka,
    // da radi ispravno i kada tab nije aktivan
    useEffect(() => {
        if (status !== SEAT_STATUS.RESERVED || !seat.reservationExpireSec) {
            return;
        }

        // expiresAt = sada + PREOSTALO vreme
        const expiresAt = Date.now() + seat.reservationExpireSec * 1000;

        const tick = () => {
            const diffMs = expiresAt - Date.now();
            const diffSec = Math.ceil(diffMs / 1000);

            if (diffSec <= 0) {
                setRemainingTime(0);
                if (onTimerEnd) {
                    onTimerEnd(seat.id);
                }
                return;
            }

            setRemainingTime(diffSec);
        };

        // odmah jedno ažuriranje
        tick();
        const intervalId = setInterval(tick, 1000);

        return () => clearInterval(intervalId);
    }, [status, seat.id, seat.reservationExpireSec, onTimerEnd]);

    const getSeatColor = () => {
        switch (status) {
            case SEAT_STATUS.SELECTED:
                return '#3366FF';
            case SEAT_STATUS.OCCUPIED:
                return '#ff1313';
            case SEAT_STATUS.RESERVED:
                return '#ffa833';
            case SEAT_STATUS.AVAILABLE:
            case SEAT_STATUS.DISABLED:
                return '#ced4da';
            default:
                return '#6c757d';
        }
    };

    const getTextColor = () =>
        status === SEAT_STATUS.SELECTED ||
        status === SEAT_STATUS.OCCUPIED ||
        status === SEAT_STATUS.RESERVED
            ? '#fff'
            : '#212529';

    const radius = 20;
    const circumference = 2 * Math.PI * radius;

    // progress = remaining / 180 (max)
    const progressRaw = remainingTime / MAX_RESERVATION_DURATION;
    const progress = Math.max(0, Math.min(1, progressRaw)); // clamp [0,1]
    const strokeOffset = circumference * (1 - progress);

    const disabledLineStyle = isDisabled
        ? {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '-10%',
            width: '120%',
            height: '2px',
            backgroundColor: '#6c757d',
            transform: 'rotate(-45deg)',
        }
        : {};

    return (
        <motion.div
            variants={seatVariants}
            whileHover={{
                scale: isSelectable ? 1.15 : 1,
                y: isSelectable ? -4 : 0,
            }}
            whileTap={{ scale: isSelectable ? 0.95 : 1 }}
            transition={{ type: 'spring', stiffness: 400, damping: 17 }}
        >
            <Box
                disabled={isDisabled}
                onClick={isSelectable ? () => onSelect(seat.id) : null}
                sx={{
                    position: 'relative',
                    width: '45px',
                    height: '45px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: isSelectable
                        ? 'pointer'
                        : isTaken
                            ? 'not-allowed'
                            : 'default',
                    borderRadius: '15px',
                    backgroundColor: getSeatColor(),
                    border: '1px solid',
                    borderColor: getSeatColor(),
                    overflow: 'hidden',
                    opacity: isDisabled ? 0.7 : 1,
                    '&::after': disabledLineStyle,
                }}
            >
                {status === SEAT_STATUS.RESERVED && remainingTime > 0 && (
                    <Box
                        component="svg"
                        viewBox="0 0 50 50"
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            transform: 'rotate(-90deg)',
                            transformOrigin: 'center',
                        }}
                    >
                        <Box
                            component="circle"
                            cx="25"
                            cy="25"
                            r={radius}
                            fill="transparent"
                            stroke="rgba(255, 255, 255, 0.2)"
                            strokeWidth="4"
                        />
                        <Box
                            component="circle"
                            cx="25"
                            cy="25"
                            r={radius}
                            fill="transparent"
                            stroke="#FFFFFF"
                            strokeWidth="4"
                            strokeDasharray={circumference}
                            strokeDashoffset={strokeOffset}
                            sx={{ transition: 'stroke-dashoffset 0.5s linear' }}
                        />
                    </Box>
                )}
                <Typography
                    sx={{
                        position: 'relative',
                        color: getTextColor(),
                        fontWeight: 'bold',
                        fontSize: '0.7rem',
                        userSelect: 'none',
                        opacity: isDisabled ? 0.8 : 1,
                    }}
                >
                    {seat.id}
                </Typography>
            </Box>
        </motion.div>
    );
};
